import { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import styles from "./UnlockParadises.module.css";
import useIsDesktop from "@/hooks/useIsDesktop";
import { MdChevronRight, MdFlight, MdLocationOn } from "react-icons/md";
import { IoLanguageOutline, IoTimeSharp } from "react-icons/io5";
import { AiFillDollarCircle } from "react-icons/ai";
import { FaCalendarAlt } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchUnlockParadise } from "@/store/slices/holidaySlice";
import { find, map } from "lodash";
import { TbTimezone } from "react-icons/tb";
import { withRegionPath } from "@/utils/session";
import { beginListingSearch } from "@/utils/listingFilterSession";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";

function ParadiseInfoCard({ card, activeCountry, className = "" }) {
  return (
    <div
      className={`${styles.infoFloatCard} ${className}`}
      style={{ background: card.bg }}
    >
      <span className={`${styles.infoIcon} ${card.tone}`}>{card.icon}</span>
      <div className={styles.infoCardCopy}>
        <p className={styles.infoLabel}>{card.label}</p>
        <p className={styles.infoValue}>{activeCountry?.[card.key]}</p>
      </div>
    </div>
  );
}

const INFO_SLIDER_SETTINGS = {
  dots: true,
  infinite: false,
  speed: 400,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  swipe: true,
  draggable: true,
  touchMove: true,
};

export default function UnlockParadises({ initialUnlockParadise }) {
  const dispatch = useAppDispatch();
  const { isDesktop, mounted } = useIsDesktop(992);
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeCountryId, setActiveCountryId] = useState(null);

  const { unlockParadise: storeParadise, loading } = useAppSelector((state) => state.holiday);
  const unlockParadise = storeParadise || initialUnlockParadise;

  useEffect(() => {
    if (initialUnlockParadise) return;
    dispatch(fetchUnlockParadise());
  }, [dispatch, initialUnlockParadise]);

  const data = useMemo(() => {
    const list = unlockParadise;
    if (Array.isArray(list) && list.length) {
      setActiveRegion(list[0])
      return list;
    }
    if (!loading || initialUnlockParadise) return [];
    return [];
  }, [unlockParadise, loading, initialUnlockParadise]);

  const activeCountry = useMemo(() => {
    return (
      find(activeRegion?.countryList, (c) => c.id === activeCountryId) || activeRegion?.countryList?.[0] || null
    );
  }, [activeRegion, activeCountryId]);

  const isLoaded = Boolean(initialUnlockParadise) || !loading;
  const hasItems = data.length > 0;
  const showEmpty = isLoaded && !hasItems;

  const handleRegionChange = (id) => {
    const region = find(data, d => d.id === parseInt(id))
    
    setActiveRegion(region);
    setActiveCountryId(region?.countryList?.[0]?.id ?? null);
  };

  const infoCards = [
    {
      id: "best",
      label: "Best Time To Visit",
      key: "bestTimeToVisit",
      icon: <FaCalendarAlt size={20} />,
      bg: "#FEF9F6",
      tone: styles.toneOrange,
    },
    {
      id: "budget",
      label: "Average Budget",
      key: "averageBudget",
      icon: <AiFillDollarCircle size={22} />,
      bg: "#F8FBF7",
      tone: styles.toneGreen,
    },
    {
      id: "flight",
      label: `Flight Time From ${activeCountry?.flightTimeFrom || "UAE"}`,
      key: "flightTime",
      icon: <MdFlight size={22} />,
      bg: "#F9F9FE",
      tone: styles.tonePurple,
    },
    {
      id: "time",
      label: "Time Difference",
      key: "timeDifference",
      icon: <TbTimezone size={22} />,
      bg: "#FEFBF5",
      tone: styles.toneYellow,
    },
  ];

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.heading}>
        <h2 className={"standard-heading"}>Unlock Hidden Paradises</h2>
        <p className="standard-subheading">Travel your way beautifully!</p>
      </div>

      {/* Mobile region tabs */}
      {hasItems ? (
      <div className={styles.regionTabs} role="tablist" aria-label="Regions">
        {map(data, (region) => (
          <button
            key={region.id}
            type="button"
            role="tab"
            aria-selected={activeRegion?.id === region.id}
            className={`${styles.regionTab} ${activeRegion?.id === region.id ? styles.regionTabActive : ""
              }`}
            onClick={() => handleRegionChange(region.id)}
          >
            {region.name}
          </button>
        ))}
      </div>
      ) : null}

      {/* Mobile country cards */}
      {activeCountry ? (
      <div className={styles.countryCards}>
        {map(activeRegion?.countryList, (country) => (
          <button
            key={country.id}
            type="button"
            className={`${styles.countryCard} ${activeCountry.id === country.id ? styles.countryCardActive : ""
              }`}
            onClick={() => setActiveCountryId(country.id)}
          >
            <img
              src={country.countryLogo}
              alt=""
              className={styles.countryCardFlag}
            />
            <span>{country.name}</span>
          </button>
        ))}
      </div>
      ) : null}

      {showEmpty ? (
        <p className="nodata">No data found</p>
      ) : (
      <div className={styles.layout}>
        {/* Desktop sidebar */}
        <aside className={styles.sidebar}>
          <label className={styles.selectWrap}>
            <span className={styles.srOnly}>Select region</span>
            <select
              className={styles.regionSelect}
              value={activeRegion?.id}
              onChange={(e) => handleRegionChange(e.target.value)}
            >
              {map(data, (region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </label>

          <ul className={styles.countryList}>
            {activeRegion?.countryList.map((country) => (
              <li key={country.id}>
                <button
                  type="button"
                  className={`${styles.countryItem} ${activeCountry.id === country.id ? styles.countryItemActive : ""
                    }`}
                  onClick={() => setActiveCountryId(country.id)}
                >
                  <span className={styles.countryItemLeft}>
                    <img
                      src={country.countryLogo}
                      alt=""
                      className={styles.flag}
                    />
                    {country.name}
                  </span>
                  <div className={styles.countryArrow}>
                    <MdChevronRight size={30} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>
        {activeCountry &&
          <div className={styles.main}>
            <div className={styles.heroBlock} style={{ backgroundImage: `linear-gradient(rgb(0 0 0 / 0%), rgb(18 18 18 / 70%)), url(${activeCountry.imagePath})` }}>
              <div className={styles.hero}>
                <div className={styles.heroContent}>
                  <h3 className={styles.heroTitle}>{activeCountry.name}</h3>
                  <p className={styles.heroDesc}>{activeCountry.subTitle}</p>
                  <div className={styles.metaChips}>
                    <div className={styles.chip}>
                      <span className={styles.chipHead}><MdLocationOn style={{ marginBottom: "-2px" }} /> Capital</span>
                      <p className={styles.chipdesc}>{activeCountry.capital}</p>
                    </div>
                    <div className={styles.chip}>
                      <span className={styles.chipHead}><IoLanguageOutline style={{ marginBottom: "-2px" }} /> Language</span>
                      <p className={styles.chipdesc}>{activeCountry.language}</p>
                    </div>
                    <div className={styles.chip}>
                      <span className={styles.chipHead}><AiFillDollarCircle style={{ marginBottom: "-2px" }} /> Currency</span>
                      <p className={styles.chipdesc}>{activeCountry.currency}</p>
                    </div>
                  </div>
                </div>
                <div className={styles.infoFloat}>
                  {infoCards.map((card) => (
                    <ParadiseInfoCard
                      key={card.id}
                      card={card}
                      activeCountry={activeCountry}
                    />
                  ))}
                </div>
              </div>
            </div>

            {mounted && !isDesktop ? (
              <div className={styles.infoSliderWrap}>
                <Slider
                  key={`${activeRegion?.id}-${activeCountry?.id}`}
                  {...INFO_SLIDER_SETTINGS}
                  dotsClass={`slick-dots ${styles.infoCarouselDots}`}
                  appendDots={appendLoopingSlickDots(`slick-dots ${styles.infoCarouselDots}`)}
                >
                  {infoCards.map((card) => (
                    <div key={card.id} className={styles.infoSlide}>
                      <ParadiseInfoCard card={card} activeCountry={activeCountry} />
                    </div>
                  ))}
                </Slider>
              </div>
            ) : null}

            <div className={styles.categoryGrid}>
              {map(activeCountry.packageTypesList, (cat) => (
                <a key={cat.id} href={withRegionPath(`/holiday/list?CountryId=${activeCountry.id}&CountryName=${activeCountry.name}&PackageTypeId=${cat.packageTypeId}`)} className={styles.categoryCard} onClick={() => beginListingSearch("holiday")}>
                  <img src={cat.icon} alt={cat.label} className={styles.categoryImage} />
                  <div className={styles.categoryOverlay} />
                  <span className={styles.categoryLabel}>{cat.packageTypeName}</span>
                </a>
              ))}
            </div>
          </div>
        }
      </div>
      )}
    </section>
  );
}
